import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Typography, Box, Divider } from '@mui/material';
import PartnerAppLink from './PartnerAppLink';

const linkProSx = {
  color: '#8B7D6B',
  fontWeight: 'bold',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
};

const PartnersPage = () => {
  const location = useLocation();

  useEffect(() => {
    const raw = (location.hash || '').replace(/^#/, '');
    if (!raw) return;
    const id = raw.split('?')[0];
    const el = document.getElementById(id);
    if (!el) return;
    const scrollToAnchor = () => {
      // Липкая шапка + строка плиток на мобильных выше, чем scroll-margin у секции;
      // scrollIntoView(block:start) уводит заголовок под навбар — считаем отступ вручную.
      const isMobile = window.matchMedia('(max-width: 899.95px)').matches;
      const headerOffset = isMobile ? 168 : 96;
      const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    const t = window.setTimeout(() => {
      requestAnimationFrame(scrollToAnchor);
    }, 80);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <Container sx={{ py: 4, maxWidth: 'md' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
        Партнерам
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}>
        Здесь собрана информация о том, как сдать оборудование в аренду, как разместить товар и как разместить услугу через
        BERLOGA PRO.
      </Typography>

      <Box component="section">
        <Typography
          id="equipment"
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 'bold',
            color: '#333',
            mb: 2,
            scrollMarginTop: { xs: 168, md: 96 },
          }}
        >
          Сдать своё оборудование
        </Typography>
        <Box sx={{ backgroundColor: '#F5F3F0', p: 3, borderRadius: 2 }}>
          <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8 }}>
            Вы можете сдавать своё оборудование в аренду через наш сервис. Для этого нужно добавить актив в приложении{' '}
            <PartnerAppLink to="/landlord" sx={linkProSx}>
              BERLOGA PRO
            </PartnerAppLink>
            {' '}
            и привезти его в выбранный Вами при добавлении центр выдачи. Владелец оборудования получает 30% от стоимости
            аренды и может сам пользоваться оборудованием, при условии, что оно не забронировано на выбранные даты.
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 2, fontStyle: 'italic' }}>
            * Логин и пароль тот же, что и в данном приложении.
          </Typography>
          <Typography variant="body2" sx={{ color: '#333', mt: 2, lineHeight: 1.6 }}>
            Если у вас возникли вопросы — пишите нам в телеграм{' '}
            <Typography component="a" href="https://t.me/iloveberloga" target="_blank" rel="noopener noreferrer" sx={linkProSx}>
              @iloveberloga
            </Typography>
            .
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box component="section">
        <Typography
          id="product"
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 'bold',
            color: '#333',
            mb: 2,
            scrollMarginTop: { xs: 168, md: 96 },
          }}
        >
          Разместить товар
        </Typography>
        <Box sx={{ backgroundColor: '#F5F3F0', p: 3, borderRadius: 2 }}>
          <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8 }}>
            Вы можете продавать различные товары (в том числе личные вещи) через наш сервис. Для этого нужно добавить товар
            в приложении{' '}
            <PartnerAppLink to="/seller" sx={linkProSx}>
              BERLOGA PRO
            </PartnerAppLink>
            {' '}
            и привезти его в выбранный Вами при добавлении центр выдачи. Комиссия площадки зависит от категории товара.
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 2, fontStyle: 'italic' }}>
            * Логин и пароль тот же, что и в данном приложении.
          </Typography>
          <Typography variant="body2" sx={{ color: '#333', mt: 2, lineHeight: 1.6 }}>
            Если у вас возникли вопросы — пишите нам в телеграм{' '}
            <Typography component="a" href="https://t.me/iloveberloga" target="_blank" rel="noopener noreferrer" sx={linkProSx}>
              @iloveberloga
            </Typography>
            .
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box component="section">
        <Typography
          id="service"
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 'bold',
            color: '#333',
            mb: 2,
            scrollMarginTop: { xs: 168, md: 96 },
          }}
        >
          Разместить услугу
        </Typography>
        <Box sx={{ backgroundColor: '#F5F3F0', p: 3, borderRadius: 2 }}>
          <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8 }}>
            Для размещения услуги вам нужно перейти в{' '}
            <PartnerAppLink to="/master" sx={linkProSx}>
              BERLOGA PRO
            </PartnerAppLink>
            , нажать кнопку «Добавить услугу» и следовать инструкциям.
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 2, fontStyle: 'italic' }}>
            * Логин и пароль тот же, что и в данном приложении.
          </Typography>
          <Typography variant="body2" sx={{ color: '#333', mt: 2, lineHeight: 1.6 }}>
            Если у вас возникли вопросы — пишите нам в телеграм{' '}
            <Typography component="a" href="https://t.me/iloveberloga" target="_blank" rel="noopener noreferrer" sx={linkProSx}>
              @iloveberloga
            </Typography>
            .
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default PartnersPage;
